import { useLayoutEffect, useRef, type JSX } from 'react';
import { useI18n } from '../i18n/context.js';
import type { BuilderStep } from './steps.js';

export function BuilderStepNavigation({
  steps,
  activeStep,
  onSelect,
}: {
  readonly steps: readonly BuilderStep[];
  readonly activeStep: string;
  readonly onSelect: (stepId: string) => void;
}): JSX.Element {
  const { t } = useI18n();
  const navigation = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const nav = navigation.current;
    const current = nav?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!nav || !current) return;
    // Move only the horizontal strip: the page keeps its own step/back scroll position.
    const reveal = (): void => {
      const left = current.offsetLeft;
      const right = left + current.offsetWidth;
      if (left < nav.scrollLeft || right > nav.scrollLeft + nav.clientWidth)
        nav.scrollLeft = Math.max(0, left - (nav.clientWidth - current.offsetWidth) / 2);
    };
    reveal();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(reveal);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [activeStep]);

  return (
    <nav
      ref={navigation}
      aria-label={t.builder.navigationLabel}
      className="relative -mx-4 mb-5 overflow-x-auto px-4 py-1"
    >
      <ol className="flex min-w-max gap-2">
        {steps.map((step, index) => {
          const current = step.id === activeStep;
          return (
            <li key={step.id}>
              <button
                type="button"
                aria-current={current ? 'step' : undefined}
                onClick={() => onSelect(step.id)}
                className={`grid gap-0.5 rounded-xl border px-3 py-1.5 text-left text-sm whitespace-nowrap ${
                  current
                    ? 'border-[var(--kitup-color-primary)] bg-[color-mix(in_srgb,var(--kitup-color-primary)_10%,white)]'
                    : 'border-[var(--kitup-color-border)] bg-[var(--kitup-color-paper)]'
                }`}
              >
                <span className={current ? 'font-semibold' : 'font-medium'}>
                  {index + 1}. {step.label}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-[var(--kitup-color-muted)]">
                  <span
                    aria-hidden="true"
                    className={`kitup-step-dot kitup-step-dot-${step.state}`}
                  />
                  <span>{t.builder.stepStates[step.state]}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
